import React, {useContext, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Route, useLocation} from 'react-router-dom';
import {isProjectDetailPath, reloadIfClientAssetsStale} from '../../libs/clientAssetVersion';
import {Sidebar} from '../../components';
import { ProjectAccessWrapper } from '../../components/ProjectAccessWrapper';
import {User} from '../../states/actions';
import {
  Dashboard,
  Users,
  Profile,
  Chatbots,
  ChatbotBuilder,
  Documents,
  DocumentEdit,
  EmailContents,
  ChatbotReview,
  EmailTemplates,
  Conversation,
  Bot,
  Sheets,
  Clients,
  Invoices,
  Projects,
  Inquiries,
  Leads,
  Project,
  Subscriptions,
  LegalFields,
  Settings,
  Match,
} from '../index';

import { GlobalStateContext } from '../../components/GlobalState';

export default function MainLayout() {
  const user = useSelector(User.get());
  const location = useLocation();

  // Only refresh user data on initial mount if not already loaded
  useEffect(() => {
    if (!user || !user.role) {
      User.forceRefresh();
    }
  }, []); // Empty dependency array - only run on mount

  useEffect(() => {
    if (isProjectDetailPath(location.pathname)) {
      reloadIfClientAssetsStale();
    }
  }, [location.pathname]);
  
  const pages = [
    {
      path: '/',
      /* component: Dashboard, */
      component: Projects,
      roles: ['admin', 'lawyer', 'support'],
    },
    {
      path: '/subscriptions',
      component: Subscriptions,
      roles: ['admin'],
    },
    {
      path: '/clients',
      component: Clients,
      roles: ['admin', 'support'],
    },
    {
      path: '/client/:clientId',
      component: Clients,
      roles: ['admin', 'support'],
    },
    {
      path: '/invoices',
      component: Invoices,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/projects',
      component: Projects,
      roles: ['admin', 'lawyer', 'support'],
    },
    {
      path: '/inquiries',
      component: Inquiries,
      roles: ['admin'],
    },
    {
      path: '/leads',
      component: Leads,
      roles: ['admin', 'support'],
    },
    {
      path: '/projects/:projectNo',
      component: ProjectAccessWrapper,
      roles: ['admin', 'lawyer', 'support'],
    },
    {
      path: '/users',
      component: Users,
      roles: ['admin'],
    },
    {
      path: '/users/:userId',
      component: Users,
      roles: ['admin'],
    },
    {
      path: '/legal-fields',
      component: LegalFields,
      roles: ['admin'],
    },
    {
      path: '/match',
      component: Match,
      roles: ['admin'],
    },
    {
      path: '/settings',
      component: Settings,
      roles: ['admin'],
    },
    {
      path: '/profile',
      component: Profile,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/email-contents',
      component: EmailContents,
      roles: ['admin'],
    },
    {
      path: '/email-contents/:emailContentId',
      component: EmailContents,
      roles: ['admin'],
    },
    {
      path: '/chatbots',
      component: Chatbots,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/chatbots/:chatbotId?',
      component: Bot,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/chatbot-review/:submissionId',
      component: ChatbotReview,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/chatbot-builder/:chatbotId/:redirectId?',
      component: ChatbotBuilder,
      roles: ['admin', 'lawyer'],
    },

    {
      path: '/documents',
      component: Documents,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/sheets',
      component: Sheets,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/user/document/:documentId',
      component: DocumentEdit,
      roles: ['admin', 'lawyer'],
    },
    {
      path: '/email-templates',
      component: EmailTemplates,
      roles: ['admin'],
    },
    {
      path: '/email-templates/:emailTemplateId',
      component: EmailTemplates,
      roles: ['admin'],
    },
    {
      path: '/chatbot/conversation/:chatbotId',
      component: Conversation,
      roles: ['admin'],
    },
  ];
  
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  // Memoize the main content area class to prevent unnecessary re-renders
  const mainContentClass = useMemo(() => 
    `${globalState.minmenu ? 'large' : ''} main_content_area`, 
    [globalState.minmenu]
  );
  
  return (
    <>
      <div className="container body">
        <div className="main_container">
          <Sidebar />

          <div className={mainContentClass} role="main">
            {pages.map((page, index) => {
              if (page.roles.includes(user.role)) {
                return <Route key={index} exact={page.path !== '/projects/:projectNo'} path={page.path} component={page.component} />;
              } else {
                return (
                  <Route
                    key={index}
                    exact={page.path !== '/projects/:projectNo'}
                    path={page.path}
                    children={<h2>You are not authorized to view this page</h2>}
                  />
                );
              }
            })}
          </div>
        </div>
      </div>
    </>
  );
}
